// ============================================
// COMMUNITY HUB — CENTRALIZED API LAYER
// ============================================

import type {
  ApiResponse,
  Community,
  Post,
  Comment,
  Transaction,
  EarningsSummary,
  PaginationMeta,
  SocialLink,
} from './types';

// ─── CONFIGURATION ─────────────────────────
const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://emailai-backend-658e.onrender.com/api';
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // ms
const REQUEST_TIMEOUT = 30000; // 30 seconds

// ─── TOKEN MANAGEMENT ──────────────────────
class TokenManager {
  private static instance: TokenManager;
  private refreshPromise: Promise<string> | null = null;

  static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  getToken(): string {
    return (
      localStorage.getItem('accessToken') ||
      localStorage.getItem('token') ||
      ''
    );
  }

  setToken(token: string): void {
    localStorage.setItem('accessToken', token);
  }

  clearToken(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('token');
  }

  async refreshToken(): Promise<string> {
    // Deduplicate concurrent refresh calls
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) throw new Error('No refresh token');

        const response = await fetch(`${API_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });

        if (!response.ok) throw new Error('Token refresh failed');

        const data = await response.json();
        this.setToken(data.accessToken);
        return data.accessToken;
      } catch (error) {
        this.clearToken();
        window.location.href = '/login?session_expired=true';
        throw error;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      return payload.exp ? payload.exp < now + 60 : false; // 60s buffer
    } catch {
      return true;
    }
  }
}

const tokenManager = TokenManager.getInstance();

// ─── FETCH WRAPPER ─────────────────────────
interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: any;
  timeout?: number;
  retries?: number;
}

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function apiRequest<T = any>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> {
  const {
    body,
    timeout = REQUEST_TIMEOUT,
    retries = MAX_RETRIES,
    ...initOptions
  } = options;

  let token = tokenManager.getToken();
  const headers: Record<string, string> = {
    ...(initOptions.headers as Record<string, string>),
  };

  // Auth header
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Content-Type for JSON body
  if (body && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const fetchOptions: RequestInit = {
    ...initOptions,
    headers,
    signal: controller.signal,
    body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
  };

  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(`${API_URL}${endpoint}`, fetchOptions);
      clearTimeout(timeoutId);

      // Token expired — refresh and retry once
      if (response.status === 401 && token && tokenManager.isTokenExpired(token)) {
        token = await tokenManager.refreshToken();
        headers['Authorization'] = `Bearer ${token}`;
        const retryResponse = await fetch(`${API_URL}${endpoint}`, {
          ...fetchOptions,
          headers,
        });
        clearTimeout(timeoutId);
        return handleResponse<T>(retryResponse);
      }

      return handleResponse<T>(response);
    } catch (error: any) {
      clearTimeout(timeoutId);
      lastError = error;

      // Don't retry if:
      // - Request was aborted (timeout)
      // - It's a 4xx error (client error)
      // - We've exhausted retries
      if (
        error.name === 'AbortError' ||
        (error instanceof ApiError && error.status < 500) ||
        attempt >= retries
      ) {
        break;
      }

      // Exponential backoff
      await new Promise(resolve =>
        setTimeout(resolve, RETRY_DELAY * Math.pow(2, attempt))
      );
    }
  }

  throw lastError || new Error('Request failed');
}

async function handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
  const contentType = response.headers.get('content-type');
  const isJson = contentType?.includes('application/json');

  if (!response.ok) {
    const errorData = isJson ? await response.json() : {};
    
    // Rate limit handling
    if (response.status === 429) {
      const retryAfter = response.headers.get('Retry-After');
      const waitTime = retryAfter ? parseInt(retryAfter) * 1000 : 5000;
      console.warn(`Rate limited. Retry after ${waitTime}ms`);
      throw new ApiError(429, 'Too many requests. Please slow down.', errorData);
    }

    throw new ApiError(
      response.status,
      errorData.error || errorData.message || `Request failed with status ${response.status}`,
      errorData
    );
  }

  if (!isJson) {
    return { success: true, data: response as unknown as T };
  }

  return response.json();
}

// ─── COMMUNITY API ─────────────────────────
export const communityApi = {
  // Fetch all communities with filters
  async getCommunities(params: {
    search?: string;
    category?: string;
    filter?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<ApiResponse<{ communities: Community[]; pagination: PaginationMeta }>> {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.category && params.category !== 'all') query.set('category', params.category);
    if (params.filter) query.set('filter', params.filter);
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    
    const qs = query.toString();
    return apiRequest(`/api/community${qs ? `?${qs}` : ''}`);
  },

  // Get single community with posts
  async getCommunity(id: string): Promise<ApiResponse<{ community: Community; posts: Post[]; isMember: boolean; isAdmin: boolean; pagination: PaginationMeta }>> {
    return apiRequest(`/api/community/${id}`);
  },

  // Create community
  async createCommunity(data: {
    name: string;
    description: string;
    category: string;
    visibility: string;
    isMonetized?: boolean;
    subscriptionPrice?: number;
    tags?: string[];
  }): Promise<ApiResponse<Community>> {
    return apiRequest('/community/create', {
      method: 'POST',
      body: data,
    });
  },

  // Update community
  async updateCommunity(id: string, data: Partial<Community>): Promise<ApiResponse<Community>> {
    return apiRequest(`/api/community/${id}`, {
      method: 'PUT',
      body: data,
    });
  },

  // Delete community
  async deleteCommunity(id: string): Promise<ApiResponse<void>> {
    return apiRequest(`/api/community/${id}`, {
      method: 'DELETE',
    });
  },

  // Join community
  async joinCommunity(id: string): Promise<ApiResponse<{ isMember: boolean }>> {
    return apiRequest(`/api/community/${id}/join`, {
      method: 'POST',
    });
  },

  // Leave community
  async leaveCommunity(id: string): Promise<ApiResponse<{ isMember: boolean }>> {
    return apiRequest(`/api/community/${id}/leave`, {
      method: 'POST',
    });
  },

  // Get members
  async getMembers(id: string, page = 1, limit = 20): Promise<ApiResponse<{ members: any[]; pagination: PaginationMeta }>> {
    return apiRequest(`/api/community/${id}/members?page=${page}&limit=${limit}`);
  },

  // Update member role
  async updateMemberRole(communityId: string, memberId: string, role: string): Promise<ApiResponse<void>> {
    return apiRequest(`/api/community/${communityId}/members/${memberId}/role`, {
      method: 'PUT',
      body: { role },
    });
  },

  // Ban member
  async banMember(communityId: string, memberId: string, reason?: string): Promise<ApiResponse<void>> {
    return apiRequest(`/api/community/${communityId}/members/${memberId}/ban`, {
      method: 'POST',
      body: { reason },
    });
  },

  // Unban member
  async unbanMember(communityId: string, memberId: string): Promise<ApiResponse<void>> {
    return apiRequest(`/api/community/${communityId}/members/${memberId}/unban`, {
      method: 'POST',
    });
  },

  // Social links
  async updateSocialLinks(communityId: string, links: SocialLink[]): Promise<ApiResponse<SocialLink[]>> {
    return apiRequest(`/api/community/${communityId}/social-links`, {
      method: 'PUT',
      body: { links },
    });
  },
};

// ─── POSTS API ─────────────────────────────
export const postApi = {
  // Create post
  async createPost(communityId: string, data: {
    type: string;
    content?: string;
    mediaUrls?: string[];
    poll?: any;
    event?: any;
    scheduledFor?: string;
  }): Promise<ApiResponse<Post>> {
    return apiRequest(`/api/community/${communityId}/post`, {
      method: 'POST',
      body: data,
    });
  },

  // Update post
  async updatePost(postId: string, data: { content?: string }): Promise<ApiResponse<Post>> {
    return apiRequest(`/api/community/post/${postId}`, {
      method: 'PUT',
      body: data,
    });
  },

  // Delete post (soft delete)
  async deletePost(postId: string): Promise<ApiResponse<void>> {
    return apiRequest(`/api/community/post/${postId}`, {
      method: 'DELETE',
    });
  },

  // Like/Unlike post
  async toggleLike(postId: string): Promise<ApiResponse<{ liked: boolean; likeCount: number }>> {
    return apiRequest(`/api/community/post/${postId}/like`, {
      method: 'POST',
    });
  },

  // Pin/Unpin post
  async togglePin(postId: string): Promise<ApiResponse<{ pinned: boolean }>> {
    return apiRequest(`/api/community/post/${postId}/pin`, {
      method: 'POST',
    });
  },

  // Vote on poll
  async votePoll(postId: string, optionId: string): Promise<ApiResponse<{ poll: any }>> {
    return apiRequest(`/api/community/post/${postId}/vote`, {
      method: 'POST',
      body: { optionId },
    });
  },

  // RSVP to event
  async rsvpEvent(postId: string): Promise<ApiResponse<{ attending: boolean; attendeeCount: number }>> {
    return apiRequest(`/api/community/post/${postId}/rsvp`, {
      method: 'POST',
    });
  },

  // Report post
  async reportPost(postId: string, reason: string): Promise<ApiResponse<void>> {
    return apiRequest(`/api/community/post/${postId}/report`, {
      method: 'POST',
      body: { reason },
    });
  },
};

// ─── COMMENTS API ──────────────────────────
export const commentApi = {
  // Get comments for post
  async getComments(postId: string, page = 1): Promise<ApiResponse<{ comments: Comment[]; pagination: PaginationMeta }>> {
    return apiRequest(`/api/community/post/${postId}/comments?page=${page}`);
  },

  // Add comment
  async addComment(postId: string, data: { content: string; parentCommentId?: string }): Promise<ApiResponse<Comment>> {
    return apiRequest(`/api/community/post/${postId}/comment`, {
      method: 'POST',
      body: data,
    });
  },

  // Edit comment
  async editComment(commentId: string, content: string): Promise<ApiResponse<Comment>> {
    return apiRequest(`/api/community/comment/${commentId}`, {
      method: 'PUT',
      body: { content },
    });
  },

  // Delete comment
  async deleteComment(commentId: string): Promise<ApiResponse<void>> {
    return apiRequest(`/api/community/comment/${commentId}`, {
      method: 'DELETE',
    });
  },

  // Like comment
  async toggleLikeComment(commentId: string): Promise<ApiResponse<{ liked: boolean }>> {
    return apiRequest(`/api/community/comment/${commentId}/like`, {
      method: 'POST',
    });
  },
};

// ─── UPLOAD API ────────────────────────────
export const uploadApi = {
  async uploadImage(file: File): Promise<ApiResponse<{ url: string }>> {
    const formData = new FormData();
    formData.append('image', file);
    return apiRequest('/upload/image', {
      method: 'POST',
      body: formData,
    });
  },

  async uploadFile(file: File): Promise<ApiResponse<{ url: string; name: string; size: number }>> {
    const formData = new FormData();
    formData.append('file', file);
    return apiRequest('/upload/file', {
      method: 'POST',
      body: formData,
    });
  },
};

// ─── MONETIZATION API ──────────────────────
export const monetizationApi = {
  // Get earnings summary
  async getEarningsSummary(communityId: string, period: string = 'monthly'): Promise<ApiResponse<EarningsSummary>> {
    return apiRequest(`/api/community/${communityId}/earnings?period=${period}`);
  },

  // Get transaction history
  async getTransactions(communityId: string, page = 1): Promise<ApiResponse<{ transactions: Transaction[]; pagination: PaginationMeta }>> {
    return apiRequest(`/api/community/${communityId}/transactions?page=${page}`);
  },

  // Set subscription price
  async setSubscriptionPrice(communityId: string, price: number, currency: string = 'USD'): Promise<ApiResponse<void>> {
    return apiRequest(`/api/community/${communityId}/subscription-price`, {
      method: 'PUT',
      body: { price, currency },
    });
  },
};

// ─── NOTIFICATIONS API ─────────────────────
export const notificationApi = {
  async getNotifications(page = 1): Promise<ApiResponse<{ notifications: any[]; unreadCount: number; pagination: PaginationMeta }>> {
    return apiRequest(`/api/notifications?page=${page}`);
  },

  async markAsRead(notificationId: string): Promise<ApiResponse<void>> {
    return apiRequest(`/api/notifications/${notificationId}/read`, {
      method: 'POST',
    });
  },

  async markAllAsRead(): Promise<ApiResponse<void>> {
    return apiRequest('/notifications/read-all', {
      method: 'POST',
    });
  },

  async getUnreadCount(): Promise<ApiResponse<{ count: number }>> {
    return apiRequest('/notifications/unread-count');
  },
};

// ─── EXPORTS ───────────────────────────────
export { tokenManager, ApiError };